export const BASE_URL = process.env.REACT_APP_API_BASE_URL 
  ? process.env.REACT_APP_API_BASE_URL.replace(/\/v1\/?$/, '').replace(/\/+$/, '')
  : (process.env.NODE_ENV === 'production'
      ? 'https://medical.homeyserve.com/api'
      : 'http://localhost:3000/api');

const API_V1 = '/v1';

export const getAuthToken = () => localStorage.getItem('token');
export const getRefreshToken = () => localStorage.getItem('refreshToken');

export const setAuthToken = (token) => {
  if (token) localStorage.setItem('token', token);
  else localStorage.removeItem('token');
};

export const setRefreshToken = (token) => {
  if (token) localStorage.setItem('refreshToken', token);
  else localStorage.removeItem('refreshToken');
};

/**
 * Checks if a given JWT token string is expired.
 * Decodes the base64 payload safely.
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payloadStr = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadStr);
    if (!payload.exp) return false;
    // Buffer by 5 seconds to prevent edge-case mid-flight expiration
    return Date.now() >= (payload.exp * 1000) - 5000;
  } catch (e) {
    return true;
  }
};

/**
 * Clears authentication tokens and redirects user to login.
 */
export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  
  // Notify listening React components to reset state
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:logout'));
    if (window.location.pathname !== '/login' && window.location.pathname !== '/forgot-password') {
      window.location.href = '/login';
    }
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Checks whether an API error represents an authentication/token failure
 */
const isAuthError = (status, errorMessage = '') => {
  if (status === 401) return true;
  if (status === 403) {
    const msg = String(errorMessage).toLowerCase();
    return (
      msg.includes('token') ||
      msg.includes('authenticate') ||
      msg.includes('unauthorized') ||
      msg.includes('expired') ||
      msg.includes('jwt')
    );
  }
  return false;
};

const fetchApi = async (endpoint, options = {}, apiVersion = API_V1) => {
  let token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  // Pre-flight client-side token expiration check
  if (token && isTokenExpired(token)) {
    const refreshToken = getRefreshToken();
    if (!refreshToken || isTokenExpired(refreshToken)) {
      logout();
      return Promise.reject(new Error('Session expired. Please log in again.'));
    }
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${BASE_URL}${apiVersion}${endpoint}`, {
      ...options,
      headers,
    });

    const contentType = response.headers.get('content-type');

    if (!response.ok) {
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        const errorMessage = errorData.message || `API Error: ${response.status}`;

        if (isAuthError(response.status, errorMessage)) {
          const refreshToken = getRefreshToken();

          // If no refresh token or refresh token is already expired, immediately logout
          if (!refreshToken || isTokenExpired(refreshToken)) {
            logout();
            return Promise.reject(new Error('Session expired. Please log in again.'));
          }

          if (!isRefreshing) {
            isRefreshing = true;
            try {
              const refreshResponse = await fetch(`${BASE_URL}${API_V1}/auth/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh_token: refreshToken }),
              });
              const refreshData = await refreshResponse.json();

              if (!refreshResponse.ok || !refreshData.access_token) {
                throw new Error('Failed to refresh token');
              }
              
              setAuthToken(refreshData.access_token);
              if (refreshData.refresh_token) {
                setRefreshToken(refreshData.refresh_token);
              }
              isRefreshing = false;
              processQueue(null, refreshData.access_token);
              
              // Retry original request with newly refreshed token
              headers['Authorization'] = `Bearer ${refreshData.access_token}`;
              return fetchApi(endpoint, { ...options, headers }, apiVersion);

            } catch (e) {
              isRefreshing = false;
              processQueue(e, null);
              logout();
              return Promise.reject(new Error('Session expired. Please log in again.'));
            }
          } else {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then(newToken => {
              headers['Authorization'] = `Bearer ${newToken}`;
              return fetchApi(endpoint, { ...options, headers }, apiVersion);
            });
          }
        }
        throw new Error(errorMessage);
      } else {
        if (response.status === 401 || response.status === 403) {
          logout();
          return Promise.reject(new Error('Session expired. Please log in again.'));
        }
        throw new Error(`Server returned an unexpected response. Status: ${response.status}`);
      }
    }

    if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
      return;
    }

    return response.json();

  } catch (error) {
    throw error;
  }
};

export const get = (endpoint, options) => {
  const { apiVersion, ...restOptions } = options || {};
  return fetchApi(endpoint, { method: 'GET', ...restOptions }, apiVersion);
};

export const post = (endpoint, body, options) => {
  const isFormData = body instanceof FormData;
  const { apiVersion, ...restOptions } = options || {};
  return fetchApi(endpoint, {
    method: 'POST',
    body: isFormData ? body : JSON.stringify(body),
    ...restOptions,
  }, apiVersion);
};

export const put = (endpoint, body, options) => {
  const isFormData = body instanceof FormData;
  const { apiVersion, ...restOptions } = options || {};
  return fetchApi(endpoint, {
    method: 'PUT',
    body: isFormData ? body : JSON.stringify(body),
    ...restOptions,
  }, apiVersion);
};

export const patch = (endpoint, body, options) => {
  const isFormData = body instanceof FormData;
  const { apiVersion, ...restOptions } = options || {};
  return fetchApi(endpoint, {
    method: 'PATCH',
    body: isFormData ? body : JSON.stringify(body),
    ...restOptions,
  }, apiVersion);
};

export const del = (endpoint, options) => {
  const { apiVersion, ...restOptions } = options || {};
  return fetchApi(endpoint, { method: 'DELETE', ...restOptions }, apiVersion);
};

export const checkPermissions = (userId) => get(`/permissions/user/${userId}`);

export const getProfile = () => fetchApi('/auth/profile');